/**
 * Create / update / delete automation rules. Each invalidates the board's rule
 * list on success. All go through the generated `TrackersService`.
 *
 * Every caller says in its own words why a request failed (see
 * `automationErrorCode`), so none of these raise the generic error toast.
 */
import { TrackersService } from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { z } from 'zod';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

import type {
  CreateTrackerAutomationDto,
  TrackerAiBudget,
  TrackerAutomationProposal,
  TrackerAutomationSuggestionRule,
  TrackerAutomation,
  TrackerAutomationList,
  TrackerAutomationRunResult,
  UpdateTrackerAutomationDto,
} from '../model/types';
import { TRACKER_AUTOMATION_QUERY_KEYS } from './query-keys';

// eslint-disable-next-line i18next/no-literal-string -- HTTP header value
const SUPPRESS_ERROR_TOAST = { headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' } };

const problemCodeSchema = z.object({ code: z.string() });

/** The stable `code` workspace-api put on a refused request, or `null` when it gave none. */
export function automationErrorCode(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const parsed = problemCodeSchema.safeParse(error.response?.data);
  return parsed.success ? parsed.data.code : null;
}

export function useCreateTrackerAutomationMutation({ trackerId }: { trackerId: string }) {
  const queryClient = useQueryClient();
  return useMutation<TrackerAutomation, Error, CreateTrackerAutomationDto>({
    mutationFn: async (body) => {
      const response = await TrackersService.createAutomation({
        client: workspaceHttp,
        path: { trackerId },
        body,
        ...SUPPRESS_ERROR_TOAST,
      });
      if (!response.data) throw new Error('Failed to create automation');
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_AUTOMATION_QUERY_KEYS.list(trackerId),
      });
    },
  });
}

export interface UpdateTrackerAutomationVariables {
  automationId: string;
  /**
   * `timeZone` is declared here as well as in the DTO so this compiles against a client
   * generated before the server accepted it; the server applies it only when the editor
   * becomes the rule's author.
   */
  patch: UpdateTrackerAutomationDto & { timeZone?: string };
}

export function useUpdateTrackerAutomationMutation({ trackerId }: { trackerId: string }) {
  const queryClient = useQueryClient();
  return useMutation<TrackerAutomation, Error, UpdateTrackerAutomationVariables>({
    mutationFn: async ({ automationId, patch }) => {
      const response = await TrackersService.updateAutomation({
        client: workspaceHttp,
        path: { trackerId, automationId },
        body: patch,
        ...SUPPRESS_ERROR_TOAST,
      });
      if (!response.data) throw new Error('Failed to update automation');
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_AUTOMATION_QUERY_KEYS.list(trackerId),
      });
    },
  });
}

/**
 * Moves a rule to `toIndex` in the board's order. The list is reordered in the
 * cache at once, since the order is also which rule wins a shared column and a
 * drop that snapped back until the refetch would read as the other answer.
 */
export function useMoveTrackerAutomationMutation({ trackerId }: { trackerId: string }) {
  const queryClient = useQueryClient();
  const queryKey = TRACKER_AUTOMATION_QUERY_KEYS.list(trackerId);
  return useMutation<
    TrackerAutomation,
    Error,
    { automationId: string; toIndex: number },
    { previous: TrackerAutomationList | undefined }
  >({
    mutationFn: async ({ automationId, toIndex }) => {
      const response = await TrackersService.updateAutomation({
        client: workspaceHttp,
        path: { trackerId, automationId },
        body: { position: toIndex },
        ...SUPPRESS_ERROR_TOAST,
      });
      if (!response.data) throw new Error('Failed to move automation');
      return response.data;
    },
    onMutate: async ({ automationId, toIndex }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<TrackerAutomationList>(queryKey);
      const moving = previous?.items.find((rule) => rule.id === automationId);
      if (previous && moving) {
        const items = previous.items.filter((rule) => rule.id !== automationId);
        items.splice(toIndex, 0, moving);
        // The builder reads which rule wins off `position`, not the list order.
        queryClient.setQueryData(queryKey, {
          ...previous,
          items: items.map((rule, position) => ({ ...rule, position })),
        });
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useDeleteTrackerAutomationMutation({ trackerId }: { trackerId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ automationId }: { automationId: string }) => {
      await TrackersService.deleteAutomation({
        client: workspaceHttp,
        path: { trackerId, automationId },
        ...SUPPRESS_ERROR_TOAST,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_AUTOMATION_QUERY_KEYS.list(trackerId),
      });
    },
  });
}

/**
 * The run only queues one job per matching row and answers before they land, so
 * the board is refreshed a few times over the following seconds instead of once.
 */
export const RUN_REFRESH_DELAYS_MS = [700, 2000, 5000] as const;

/**
 * Runs a rule on the rows that already match it. With `dryRun` it only counts
 * them, which is how the confirm says how many rows will change before any do.
 * A confirmed run calls `refreshRows` on the {@link RUN_REFRESH_DELAYS_MS}
 * schedule; the board owns how its rows are refetched.
 */
export function useRunTrackerAutomationMutation({
  trackerId,
  refreshRows,
}: {
  trackerId: string;
  refreshRows?: () => void;
}) {
  const queryClient = useQueryClient();
  return useMutation<TrackerAutomationRunResult, Error, { automationId: string; dryRun: boolean }>({
    mutationFn: async ({ automationId, dryRun }) => {
      const response = await TrackersService.runAutomation({
        client: workspaceHttp,
        path: { trackerId, automationId },
        body: { dryRun },
        ...SUPPRESS_ERROR_TOAST,
      });
      if (!response.data) throw new Error('Failed to run automation');
      return response.data;
    },
    onSuccess: (_result, { dryRun }) => {
      if (dryRun) return;
      void queryClient.invalidateQueries({
        queryKey: TRACKER_AUTOMATION_QUERY_KEYS.list(trackerId),
      });
      if (!refreshRows) return;
      for (const ms of RUN_REFRESH_DELAYS_MS) setTimeout(refreshRows, ms);
    },
  });
}

export function useUpdateTrackerAiBudgetMutation({ workspaceId }: { workspaceId: string | null }) {
  const queryClient = useQueryClient();
  return useMutation<TrackerAiBudget, Error, { amountMinor: number }>({
    mutationFn: async ({ amountMinor }) => {
      const response = await TrackersService.updateAiBudget({
        client: workspaceHttp,
        body: { monthlyCap: { amountMinor, currency: 'USD' } },
        ...SUPPRESS_ERROR_TOAST,
      });
      if (!response.data) throw new Error('Failed to update the AI budget');
      return response.data;
    },
    onSuccess: (budget) => {
      queryClient.setQueryData(TRACKER_AUTOMATION_QUERY_KEYS.aiBudget(workspaceId), budget);
      void queryClient.invalidateQueries({
        queryKey: TRACKER_AUTOMATION_QUERY_KEYS.aiCapabilities(workspaceId),
      });
    },
  });
}

/**
 * Has AI propose a rule from a sentence. Nothing is saved: the proposal goes
 * to the builder, and saving it there is the ordinary create.
 */
export function useProposeTrackerAutomationMutation({ trackerId }: { trackerId: string }) {
  return useMutation<TrackerAutomationProposal, Error, { sentence: string }>({
    mutationFn: async ({ sentence }) => {
      const response = await TrackersService.proposeAutomation({
        client: workspaceHttp,
        path: { trackerId },
        body: { sentence },
        ...SUPPRESS_ERROR_TOAST,
      });
      if (!response.data) throw new Error('Failed to propose a rule');
      return response.data;
    },
  });
}

/** Turns a suggested rule down; the board does not offer it again. */
export function useDismissTrackerAutomationSuggestionMutation({
  trackerId,
}: {
  trackerId: string;
}) {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { suggestionId: string }>({
    mutationFn: async ({ suggestionId }) => {
      const response = await TrackersService.dismissAutomationSuggestion({
        client: workspaceHttp,
        path: { trackerId, suggestionId },
        ...SUPPRESS_ERROR_TOAST,
      });
      if (response.error) throw new Error('Failed to dismiss the suggestion');
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_AUTOMATION_QUERY_KEYS.suggestions(trackerId),
      });
    },
  });
}

/**
 * Marks a suggestion taken, once the rule made from it has been created. A
 * failure here is silent: the board stops offering a pattern an existing rule
 * covers anyway.
 */
export function useAcceptTrackerAutomationSuggestionMutation({ trackerId }: { trackerId: string }) {
  const queryClient = useQueryClient();
  return useMutation<TrackerAutomationSuggestionRule, Error, { suggestionId: string }>({
    mutationFn: async ({ suggestionId }) => {
      const response = await TrackersService.acceptAutomationSuggestion({
        client: workspaceHttp,
        path: { trackerId, suggestionId },
        ...SUPPRESS_ERROR_TOAST,
      });
      if (!response.data) throw new Error('Failed to open the suggestion');
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: TRACKER_AUTOMATION_QUERY_KEYS.suggestions(trackerId),
      });
    },
  });
}
